(() => {
  'use strict';
  const normalize = value => String(value || '').trim().replace(/\s+/g, ' ').replace(/\*+$/, '').toLowerCase();
  function catalogueWords(value) {
    const q = normalize(value).replace(/\bkey[ -]?(?:chains?|rings?)\b/g, 'keyring');
    return /\bkeyring\b/.test(q) ? q.replace(/\b(?:pictures?|photos?|photographs?)\b/g, 'photo') : q;
  }
  function resolveQuery(value) {
    const q = normalize(value);
    // Supplier codes are case sensitive in Shopify's SKU index.
    if (/^(?:p\d{4}[a-z]\d{2}|[a-z]{2,5}\d[a-z\d-]{4,})$/i.test(q)) return 'variants.sku:' + q.toUpperCase();
    if (/^variants\.(?:sku|barcode):/i.test(String(value).trim())) return String(value).trim();
    if (/^(?:personalised |personalized )?(?:wine|wedding|champagne) buckets?$/.test(q)) return 'title:bucket';
    if (/^(?:personalised |personalized )?ice buckets?$/.test(q)) return 'title:ice title:bucket';
    if (/^(?:personalised |personalized )?wine coolers?$/.test(q)) return 'title:wine title:cooler';
    if (/^(?:personalised |personalized )?miniature (?:champagne )?buckets?$/.test(q)) return 'title:miniature title:bucket';
    if (/^(?:personalised |personalized )?christmas stockings?$/.test(q)) return 'title:stocking -title:box -title:chest -title:doormat -title:bauble';
    // Explicit syntax disables Shopify's broad semantic expansion. Keep native
    // field queries, exclusions and quoted phrases intact; never restrict to title only.
    if (!q || /[":]|(?:^|\s)-\S/.test(q)) return q;
    if (/\b(?:AND|OR|NOT)\b/.test(String(value))) return String(value).trim().replace(/\s+/g, ' ');
    const words = catalogueWords(q).split(' ').filter(word => /[\p{L}\p{N}]/u.test(word) && !/^(?:a|an|the|and|or|for|to|of|with|in|on|at)$/.test(word));
    return (words.length ? words : [q]).map(word => JSON.stringify(word)).join(' ');
  }
  function searchURL(value, searchPath = '/search') {
    const url = new URL(searchPath, location.origin), q = normalize(value), resolved = resolveQuery(value);
    url.searchParams.set('q', resolved);
    url.searchParams.set('type', 'product');
    url.searchParams.set('options[prefix]', 'last');
    url.searchParams.set('options[unavailable_products]', 'last');
    if (resolved !== q) url.searchParams.set('gift_query', q);
    const preview = new URL(location.href).searchParams.get('preview_theme_id');
    if (preview) url.searchParams.set('preview_theme_id', preview);
    return url;
  }
  function intent(value) {
    const q = normalize(value);
    if (/\bkeyring\b/.test(catalogueWords(q))) return /\bphoto\b/.test(catalogueWords(q)) ? 'photo_keyring' : 'keyring';
    for (const kind of ['bucket', 'stocking', 'bauble', 'christening', 'christmas', 'halloween', 'wedding', 'pet']) {
      if (q.includes(kind)) return kind;
    }
    return 'other';
  }
  function measure(event, data) {
    // Do not publish free-text queries, personalisation text, names, or email addresses.
    if (!window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.()) return;
    if (typeof window.Shopify?.analytics?.publish !== 'function') return;
    try { Promise.resolve(window.Shopify.analytics.publish(event, data)).catch(() => {}); } catch { /* Search works without analytics. */ }
  }
  function safeURL(value) {
    try {
      const url = new URL(value, location.origin);
      if (url.origin !== location.origin || !/^https?:$/.test(url.protocol)) return null;
      const preview = new URL(location.href).searchParams.get('preview_theme_id');
      if (preview) url.searchParams.set('preview_theme_id', preview);
      return url.href;
    } catch { return null; }
  }
  const cache = new Map();
  function mount(root) {
    if (root.dataset.giftSearchReady) return;
    const input = root.querySelector('input[name=q]'), form = input?.form;
    const list = root.querySelector('[data-gift-suggestions]'), status = root.querySelector('[data-gift-suggestion-status]');
    if (!input || !form || !list || !status) return;
    root.dataset.giftSearchReady = 'true';
    let timer, request, sequence = 0, active = -1;
    const overlay = root.closest('header-search');
    let opener;
    function closeOverlay() {
      if (!overlay) return;
      document.dispatchEvent(new CustomEvent('predictiveSearch:close-all'));
      opener?.focus();
    }
    if (overlay) {
      new MutationObserver(() => {
        if (overlay.classList.contains('is-active')) {
          opener = document.activeElement;
          input.focus();
        } else close();
      }).observe(overlay,{attributes:true,attributeFilter:['class']});
      root.querySelector('[data-gift-search-close]')?.addEventListener('click',closeOverlay);
    }
    const path = form.getAttribute('action') || '/search';
    const money = new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {style:'currency',currency:root.dataset.currency || 'GBP'});
    function close() {
      ++sequence; clearTimeout(timer); request?.abort(); list.hidden = true; active = -1;
      input.setAttribute('aria-expanded','false'); input.removeAttribute('aria-activedescendant');
      status.textContent = '';
    }
    function heading(text) {
      const node = document.createElement('div'); node.className = 'tps-gift-search__heading';
      node.setAttribute('role','presentation'); node.textContent = text; list.append(node);
    }
    function link(value, title, subtitle, product) {
      const href = safeURL(value); if (!href) return;
      const a = document.createElement('a'); a.href = href;
      a.id = list.id + '-' + list.querySelectorAll('[role=option]').length;
      a.setAttribute('role','option'); a.setAttribute('aria-selected','false');
      if (product) {
        const image = product.featured_image?.url || product.image;
        if (image) {
          try {
            const src = new URL(image,location.origin);
            if (src.protocol === 'https:') {
              src.searchParams.set('width','112');
              const img = document.createElement('img'); img.src = src.href; img.alt = ''; img.width = 56; img.height = 56; a.append(img);
            }
          } catch { /* A missing image does not remove the gift. */ }
        }
      }
      const copy = document.createElement('span'); copy.textContent = title;
      if (subtitle) { const sub = document.createElement('small'); sub.textContent = subtitle; copy.append(sub); }
      a.append(copy);
      if (product?.price !== undefined && product.price !== null && product.price !== '') {
        const price = Number(product.price);
        if (Number.isFinite(price)) {
          const p = document.createElement('strong');
          p.textContent = (product.price_min !== product.price_max && product.price_min != null ? 'From ' : '') + money.format(price); a.append(p);
        }
      }
      a.addEventListener('click', () => measure('tps_search_selection', {
        surface:root.dataset.surface || 'header', intent:intent(input.value),
        kind:product ? 'product' : 'discovery', ...(product ? {product_id:String(product.id)} : {})
      }));
      list.append(a);
    }
    async function suggest() {
      const q = normalize(input.value); if (q.length < 2) { close(); return; }
      const serial = ++sequence; request?.abort(); request = new AbortController();
      const resolved = resolveQuery(q);
      const lookup = resolved === 'title:bucket' ? 'ice bucket' : resolved.startsWith('title:stocking') ? 'christmas stocking' : resolved.startsWith('title:') ? resolved.replace(/title:/g,'') : /[":]|(?:^|\s)-\S/.test(q) ? q : catalogueWords(q);
      const url = new URL(path.replace(/\/$/,'') + '/suggest.json', location.origin);
      url.searchParams.set('q',lookup);
      url.searchParams.set('resources[type]','query,product,collection');
      url.searchParams.set('resources[limit]','6');
      url.searchParams.set('resources[limit_scope]','each');
      url.searchParams.set('resources[options][unavailable_products]','last');
      try {
        let data = cache.get(url.href);
        if (!data || Date.now() - data.time > 60000) {
          const response = await fetch(url,{signal:request.signal});
          if (!response.ok) throw new Error('Suggestions unavailable');
          data = {time:Date.now(),results:(await response.json()).resources?.results || {}};
          if (cache.size >= 40) cache.delete(cache.keys().next().value);
          cache.set(url.href,data);
        }
        if (serial !== sequence || normalize(input.value) !== q || document.activeElement !== input) return;
        list.replaceChildren(); active = -1; input.removeAttribute('aria-activedescendant');
        let products = data.results.products || [];
        if (resolved.includes('title:bucket')) products = products.filter(p => /bucket/i.test(p.title));
        if (resolved.startsWith('title:stocking')) products = products.filter(p => /stocking/i.test(p.title) && !/\b(box|chest|doormat|bauble)\b/i.test(p.title));
        if (products.length) {
          heading('Gifts to explore');
          products.slice(0,4).forEach(p => link(p.url,p.title,!p.available ? 'Currently unavailable' : /miniature/i.test(p.title) ? 'Miniature gift' : /ice bucket/i.test(p.title) ? 'Full-size ice bucket' : p.type || 'View personalisation options',p));
        }
        if (resolved.includes('title:bucket')) {
          heading('Choose a gift style');
          for (const [label,term] of [['Full-size ice buckets','ice bucket'],['Miniature champagne buckets','miniature champagne bucket'],['Wine coolers','wine cooler']]) link(searchURL(term,path).href,label);
        } else if (data.results.collections?.length) {
          heading('Shop collections'); data.results.collections.slice(0,2).forEach(c => link(c.url,c.title,'Explore the collection'));
        }
        const queries = data.results.queries || [];
        if (queries.length) {
          heading('Suggested searches'); queries.slice(0,2).forEach(item => {
            const text = item.text || q; link(searchURL(text,path).href,text);
          });
        }
        link(searchURL(q,path).href,'See all results for “' + q + '”');
        list.hidden = false; input.setAttribute('aria-expanded','true');
        status.textContent = list.querySelectorAll('[role=option]').length + ' suggestions. Use the up and down arrow keys to browse.';
      } catch (error) {
        if (error.name === 'AbortError' || serial !== sequence) return;
        close(); status.textContent = 'Suggestions unavailable. Press Enter to search.';
      }
    }
    input.addEventListener('input', () => { close(); timer = setTimeout(suggest,220); });
    input.addEventListener('focus',suggest);
    input.addEventListener('keydown', event => {
      if (event.key === 'Escape') { event.preventDefault(); close(); closeOverlay(); return; }
      const options = [...list.querySelectorAll('[role=option]')];
      if (list.hidden || !options.length) return;
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        active = active < 0 ? (event.key === 'ArrowDown' ? 0 : options.length - 1) : (active + (event.key === 'ArrowDown' ? 1 : -1) + options.length) % options.length;
        options.forEach((node,index) => node.setAttribute('aria-selected',String(index === active)));
        input.setAttribute('aria-activedescendant',options[active].id); options[active].scrollIntoView({block:'nearest'});
      } else if (event.key === 'Enter' && active >= 0) { event.preventDefault(); options[active].click(); }
    });
    form.addEventListener('submit', event => {
      if (!normalize(input.value)) { event.preventDefault(); return; }
      event.preventDefault();
      measure('tps_search_submit',{surface:root.dataset.surface || 'header',intent:intent(input.value)});
      location.assign(searchURL(input.value,path).href);
    });
    form.addEventListener('reset',close);
    root.addEventListener('focusout', () => setTimeout(() => { if (!root.contains(document.activeElement)) close(); },0));
    document.addEventListener('click', event => { if (!root.contains(event.target)) close(); });
  }
  function init() { document.querySelectorAll('[data-tps-gift-search]').forEach(mount); }
  window.TPSGiftSearch = {normalize,resolveQuery,searchURL,mount,measure,intent};
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded',init); else init();
  document.addEventListener('shopify:section:load',init);
  document.dispatchEvent(new CustomEvent('tps:gift-search-ready'));
})();
